import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Plus, X, PaperPlaneRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import communicationService from '../../services/communicationService';
import staffService from '../../services/staffService';
import './GroupChatPanel.css';

/**
 * GroupChatPanel - self-contained staff-to-staff group messaging surface.
 *
 * Deliberately independent from ChatContainer: a group has many
 * participants and no single "other person", so it doesn't fit
 * ChatContainer's 1:1 data model. This component owns its own list of
 * groups, its own "new group" flow, and its own message thread state.
 *
 * v1 scope: participants are picked from staff members (staffService.listStaff)
 * since a general "any user in the school" picker isn't available yet.
 */
const GroupChatPanel = () => {
  const { user } = useAuth();

  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setIsLoadingGroups(true);
      const data = await communicationService.listGroupConversations();
      const items = Array.isArray(data) ? data : (data?.items || []);
      setGroups(items);
    } catch (err) {
      console.error('Error fetching group conversations:', err);
      toast.error('Failed to load groups');
    } finally {
      setIsLoadingGroups(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      setIsLoadingMessages(true);
      const data = await communicationService.getGroupMessages(conversationId);
      const items = Array.isArray(data) ? data : (data?.items || []);
      setMessages(items);
      // Reading the thread marks it read server-side; reflect that locally
      // so the sidebar badge clears immediately instead of waiting on a refetch.
      setGroups(prev => prev.map(g => g.id === conversationId ? { ...g, unread_count: 0 } : g));
    } catch (err) {
      console.error('Error fetching group messages:', err);
      if (err.status !== 403) {
        toast.error('Failed to load messages for this group');
      }
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSelectGroup = useCallback((groupId) => {
    setSelectedGroupId(groupId);
    setMessages([]);
    fetchMessages(groupId);
  }, [fetchMessages]);

  const handleSendMessage = useCallback(async () => {
    const content = messageText.trim();
    if (!content || !selectedGroupId || isSending) return;

    try {
      setIsSending(true);
      const response = await communicationService.sendGroupMessage(selectedGroupId, content);
      setMessages(prev => [...prev, response]);
      setMessageText('');
    } catch (err) {
      console.error('Error sending group message:', err);
      toast.error(err.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, [messageText, selectedGroupId, isSending]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const openNewGroupModal = useCallback(async () => {
    setShowNewGroupModal(true);
    setNewGroupName('');
    setSelectedParticipantIds([]);
    try {
      setIsLoadingStaff(true);
      const data = await staffService.listStaff({ limit: 100 });
      const items = (data?.items || []).filter(s => s.user_id && s.user_id !== user?.id);
      setStaffOptions(items);
    } catch (err) {
      console.error('Error fetching staff for group picker:', err);
      toast.error('Failed to load staff list');
    } finally {
      setIsLoadingStaff(false);
    }
  }, [user?.id]);

  const toggleParticipant = useCallback((userId) => {
    setSelectedParticipantIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  }, []);

  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) {
      toast.error('Please enter a group name');
      return;
    }
    if (selectedParticipantIds.length < 2) {
      toast.error('Select at least 2 other members to start a group');
      return;
    }

    try {
      setIsCreatingGroup(true);
      const group = await communicationService.createGroupConversation({
        name,
        participant_ids: selectedParticipantIds,
      });
      toast.success('Group created');
      setShowNewGroupModal(false);
      await fetchGroups();
      setSelectedGroupId(group.id);
      setMessages([]);
      fetchMessages(group.id);
    } catch (err) {
      console.error('Error creating group conversation:', err);
      toast.error(err.message || 'Failed to create group');
    } finally {
      setIsCreatingGroup(false);
    }
  }, [newGroupName, selectedParticipantIds, fetchGroups, fetchMessages]);

  const selectedGroup = useMemo(
    () => groups.find(g => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="group-chat-panel">
      <div className="group-chat-sidebar">
        <div className="group-chat-sidebar-header">
          <h3>Groups</h3>
          <button className="group-new-btn" onClick={openNewGroupModal} title="Create a new group">
            <Plus size={16} /> New group
          </button>
        </div>

        <div className="group-list">
          {isLoadingGroups && groups.length === 0 && (
            <div className="group-list-loading">
              <div className="mini-spinner"></div>
              <p>Loading groups...</p>
            </div>
          )}

          {!isLoadingGroups && groups.length === 0 && (
            <div className="group-list-empty">
              <Users size={32} />
              <p>No groups yet</p>
              <button className="group-new-btn" onClick={openNewGroupModal}>
                <Plus size={16} /> Start a group
              </button>
            </div>
          )}

          {groups.map(group => (
            <div
              key={group.id}
              className={`group-list-item ${selectedGroupId === group.id ? 'active' : ''}`}
              onClick={() => handleSelectGroup(group.id)}
            >
              <div className="group-avatar">
                <Users size={18} />
              </div>
              <div className="group-list-item-info">
                <div className="group-list-item-header">
                  <h4>{group.name}</h4>
                  {group.unread_count > 0 && (
                    <span className="group-unread-badge">{group.unread_count}</span>
                  )}
                </div>
                <p className="group-participant-count">
                  {group.participant_count} member{group.participant_count !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="group-chat-content">
        {!selectedGroupId ? (
          <div className="group-chat-empty-state">
            <Users size={48} />
            <h2>No Group Selected</h2>
            <p>Pick a group from the list, or start a new one</p>
            <button className="group-new-btn" onClick={openNewGroupModal}>
              <Plus size={16} /> New group
            </button>
          </div>
        ) : (
          <>
            <div className="group-chat-header">
              <h2>{selectedGroup?.name || 'Group'}</h2>
              {selectedGroup?.participant_count != null && (
                <span className="group-chat-header-sub">
                  {selectedGroup.participant_count} member{selectedGroup.participant_count !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="group-message-thread">
              {isLoadingMessages ? (
                <div className="group-list-loading">
                  <div className="mini-spinner"></div>
                  <p>Loading messages...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="group-thread-empty">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`group-message ${msg.sender_id === user?.id ? 'group-message-sent' : 'group-message-received'}`}
                  >
                    <div className="group-message-content">
                      <div className="group-message-header">
                        <span className="group-message-sender">{msg.sender_name}</span>
                        <span className="group-message-time">{formatTime(msg.created_at)}</span>
                      </div>
                      <p className="group-message-text">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="group-message-input-row">
              <textarea
                className="group-message-textarea"
                placeholder="Type a message... (Enter to send, Shift+Enter for a new line)"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isSending}
                rows={2}
              />
              <button
                className="group-send-btn"
                onClick={handleSendMessage}
                disabled={!messageText.trim() || isSending}
                title="Send message"
              >
                <PaperPlaneRight size={18} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* New Group Modal — built as a simple inline form inside DialogModal's
          overlay shell isn't a fit here (DialogModal's `input` supports only
          a single text field, not a name + multi-select), so this renders
          its own lightweight modal reusing the same overlay/card visual
          language. DialogModal is still used for anything that needs a
          confirm-style dialog elsewhere in this panel (none currently). */}
      {showNewGroupModal && (
        <div className="group-modal-overlay" onClick={() => !isCreatingGroup && setShowNewGroupModal(false)}>
          <div className="group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="group-modal-header">
              <h3>New group</h3>
              <button
                className="group-modal-close"
                onClick={() => setShowNewGroupModal(false)}
                disabled={isCreatingGroup}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="group-modal-body">
              <label className="group-modal-label">Group name</label>
              <input
                type="text"
                className="group-modal-input"
                placeholder="e.g. Grade 5 Teachers"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                disabled={isCreatingGroup}
                autoFocus
              />

              <label className="group-modal-label">
                Members {selectedParticipantIds.length > 0 && `(${selectedParticipantIds.length} selected)`}
              </label>
              <p className="group-modal-hint">Pick at least 2 other staff members to join.</p>

              {isLoadingStaff ? (
                <div className="group-list-loading">
                  <div className="mini-spinner"></div>
                  <p>Loading staff...</p>
                </div>
              ) : (
                <div className="group-modal-staff-list">
                  {staffOptions.length === 0 && (
                    <p className="group-modal-hint">No other staff members found.</p>
                  )}
                  {staffOptions.map(staff => (
                    <label key={staff.user_id} className="group-modal-staff-item">
                      <input
                        type="checkbox"
                        checked={selectedParticipantIds.includes(staff.user_id)}
                        onChange={() => toggleParticipant(staff.user_id)}
                        disabled={isCreatingGroup}
                      />
                      <span>{staff.full_name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim()}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="group-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowNewGroupModal(false)}
                disabled={isCreatingGroup}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateGroup}
                disabled={isCreatingGroup}
              >
                {isCreatingGroup ? 'Creating...' : 'Create group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupChatPanel;
